const AUTH_ENDPOINT = 'https://accounts.google.com/o/oauth2/v2/auth';
export const GOOGLE_REDIRECT_PATH = '/public/google-redirect.html';
// Must match the channel name in public/google-redirect.html.
export const GOOGLE_CHANNEL = 'holdshort-google-signin';
const POPUP_NAME = 'holdshort-google-signin';
const POPUP_WIDTH = 500;
const POPUP_HEIGHT = 600;

export const POPUP_POLL_MS = 500;
export const CLOSED_GRACE_MS = 1500;
export const ATTEMPT_TIMEOUT_MS = 5 * 60 * 1000;

export const POPUP_BLOCKED_MESSAGE =
	'Your browser blocked the Google sign-in window. Allow pop-ups for this site and try again.';
export const CANCELLED_MESSAGE = 'Google sign-in was cancelled.';
const FAILED_MESSAGE = 'Google sign-in failed. Please try again.';
const TIMEOUT_MESSAGE = 'Google sign-in timed out. Please try again.';
const SUPERSEDED_MESSAGE = 'Google sign-in was restarted.';

let current = null;

function randomToken() {
	const bytes = window.crypto.getRandomValues(new Uint8Array(32));
	return btoa(String.fromCharCode(...bytes))
		.replace(/\+/g, '-')
		.replace(/\//g, '_')
		.replace(/=+$/, '');
}

function readNonce(idToken) {
	try {
		const payload = idToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
		// Only the ASCII `nonce` is read, so atob's Latin-1 view of the JSON is enough.
		return JSON.parse(atob(payload.padEnd(Math.ceil(payload.length / 4) * 4, '='))).nonce;
	} catch {
		return undefined;
	}
}

function buildAuthUrl(clientId, { state, nonce }) {
	const params = new URLSearchParams({
		client_id: clientId,
		redirect_uri: `${window.location.origin}${GOOGLE_REDIRECT_PATH}`,
		response_type: 'id_token',
		scope: 'openid email profile',
		state,
		nonce,
		prompt: 'select_account',
		hl: 'en'
	});
	return `${AUTH_ENDPOINT}?${params}`;
}

function popupFeatures() {
	const left = Math.max(0, Math.round(window.screenX + (window.outerWidth - POPUP_WIDTH) / 2));
	const top = Math.max(0, Math.round(window.screenY + (window.outerHeight - POPUP_HEIGHT) / 2));
	return `popup=yes,width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top}`;
}

/**
 * Signs in with Google's OIDC implicit flow in a popup and resolves with the
 * id_token. The popup opens synchronously, so call this straight from the click
 * handler, before any `await`, or iOS Safari blocks it. A new call supersedes
 * (rejects) a pending one; every attempt settles exactly once.
 *
 * @param {string} clientId
 * @returns {Promise<string>}
 */
export function signInWithGoogle(clientId) {
	current?.settle(new Error(SUPERSEDED_MESSAGE));

	return new Promise((resolve, reject) => {
		const attempt = { state: randomToken(), nonce: randomToken(), popup: null, settle: null };
		let channel = null;
		let pollTimer = null;
		let graceTimer = null;
		let backstopTimer = null;

		const receive = data => {
			if (current !== attempt || !data || data.state !== attempt.state) return;
			if (data.error) {
				attempt.settle(new Error(data.error === 'access_denied' ? CANCELLED_MESSAGE : FAILED_MESSAGE));
			} else if (typeof data.idToken === 'string' && readNonce(data.idToken) === attempt.nonce) {
				attempt.settle(null, data.idToken);
			} else {
				attempt.settle(new Error(FAILED_MESSAGE));
			}
		};
		const onWindowMessage = event => {
			if (event.origin === window.location.origin) receive(event.data);
		};

		attempt.settle = (error, idToken) => {
			if (attempt.settle === null) return;
			attempt.settle = null;
			clearInterval(pollTimer);
			clearTimeout(graceTimer);
			clearTimeout(backstopTimer);
			window.removeEventListener('message', onWindowMessage);
			channel?.close();
			if (current === attempt) current = null;
			if (error) reject(error);
			else resolve(idToken);
		};

		if (typeof BroadcastChannel === 'function') {
			channel = new BroadcastChannel(GOOGLE_CHANNEL);
			channel.onmessage = event => receive(event.data);
		}
		window.addEventListener('message', onWindowMessage);
		current = attempt;

		attempt.popup = window.open(buildAuthUrl(clientId, attempt), POPUP_NAME, popupFeatures());
		if (!attempt.popup) {
			attempt.settle(new Error(POPUP_BLOCKED_MESSAGE));
			return;
		}

		pollTimer = setInterval(() => {
			if (!attempt.popup.closed) return;
			clearInterval(pollTimer);
			// The bridge closes the popup right after posting; give that message time to land.
			graceTimer = setTimeout(() => attempt.settle?.(new Error(CANCELLED_MESSAGE)), CLOSED_GRACE_MS);
		}, POPUP_POLL_MS);

		backstopTimer = setTimeout(() => {
			attempt.popup.close();
			attempt.settle?.(new Error(TIMEOUT_MESSAGE));
		}, ATTEMPT_TIMEOUT_MS);
	});
}

/**
 * Brings the pending attempt's popup to the front.
 *
 * @returns {boolean} false when no attempt is pending or its popup is already closed.
 */
export function focusGooglePopup() {
	if (!current?.popup || current.popup.closed) return false;
	current.popup.focus();
	return true;
}
